import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit } from '@angular/core';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { ElementVisibilityService } from '@core-mkt/services/element-visibility/element-visibility.service';
import { WebinarScheduleService } from '@core-mkt/services/webinar-schedule/webinar-schedule.service';
import { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import { VisionModalService } from '@core-mkt/shared/components/vision-modal/vision-modal.service';
import { VisionVideoButton } from '@core-mkt/shared/components/vision-video-button/vision-video-button/vision-video-button';
import { VisionVideoButtonComponent } from '@core-mkt/shared/components/vision-video-button/vision-video-button/vision-video-button.component';
import type { WebinarScheduleResponse } from '@core-mkt/shared/components/webinar-schedule/webinar-schedule';
import { TopSection, VideoButton } from '../top-section';

@Component({
  selector: 'top-section-button-group',
  templateUrl: './top-section-button-group.component.html',
  standalone: true,
  imports: [CommonModule, VisionButtonComponent, VisionVideoButtonComponent],
})
export class TopSectionButtonGroupComponent implements AfterViewInit {
  @ViewChild('topSectionCta', { static: false }) topSectionCta: ElementRef;
  @Input() secondButtonData: VisionButton;
  @Input() wistiaButtonData: VisionVideoButton;
  @Input() data: TopSection;
  @Input() product: XGritCompleteProduct;
  @Input() moneyGuaranteeLabel: string;
  @Input() wistiaCtaData: VideoButton;
  @Input() ctaButtonData: VisionButton;
  @Input() showScheduleCTA: boolean;
  @Input() hasSecondButtonData: boolean;
  @Input() modalHasContent: boolean;
  @Output() ctaVideoPlayEvent = new EventEmitter();
  @Output() ctaDisplayChange = new EventEmitter<boolean>();

  private readonly webinarScheduleService = inject(WebinarScheduleService);
  private readonly modal = inject(VisionModalService);
  private readonly elementVisibility = inject(ElementVisibilityService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly platformId = inject<object>(PLATFORM_ID);

  private lastCtaDisplayValue = false;

  /**
   * Displays the webinar schedule by triggering the webinar schedule service.
   *
   * @param event - The event object associated with the user interaction.
   * @param content - The content to be passed to the webinar schedule service.
   *
   * @remarks
   * This method prevents the default behavior of the event before invoking the service.
   */
  showWebinarSchedule(event: Event, content: WebinarScheduleResponse[]): void {
    event.preventDefault();
    this.webinarScheduleService.setShowWebinarSchedule(true, content);
  }

  get outerButtonAlign(): string {
    const soloButtonWrapperClasses = 'flex sm:flex-row flex-col lg:items-start';
    if (this.showFirstButtonOnly) {
      return soloButtonWrapperClasses;
    }
    if ((this.secondButtonData || this.wistiaButtonData) && !this.showModal1CTA && !this.showCTA) {
      return soloButtonWrapperClasses;
    }
    if ((this.secondButtonData || this.wistiaButtonData) && (this.showModal1CTA || this.showCTA)) {
      return soloButtonWrapperClasses + ' flex-wrap';
    }
    return '';
  }

  get showCTA(): boolean {
    return this.product !== undefined || (this.data.ctaLink !== null && !this.data.ctaLink.includes('useModal'));
  }

  get showModal1CTA(): boolean {
    return (
      this.data.ctaLink !== null &&
      this.data.ctaLink.includes('useModal') &&
      !this.secondButtonData?.buttonLink.includes('webinar-schedule')
    );
  }

  get showModal2CTA(): boolean {
    return this.secondButtonData && this.secondButtonData.buttonLink?.includes('useModal');
  }

  get showFirstButtonOnly(): boolean {
    return (
      (this.showModal1CTA || this.showCTA || this.data?.product !== undefined) &&
      !this.wistiaCtaData &&
      !this.secondButtonData
    );
  }

  get ctaDisplay(): boolean {
    return this.elementVisibility.isElVisible;
  }

  get hasWebinarData(): boolean {
    return this.data?.webinarData && this.data.webinarData.length > 0;
  }

  get webinarButtonData(): VisionButton {
    return { ...this.secondButtonData, buttonLink: null };
  }

  get isWebinarScheduleLink(): boolean {
    return this.secondButtonData?.buttonLink?.includes('webinar-schedule') ?? false;
  }

  get useRightMargin(): boolean {
    return !this.showFirstButtonOnly && (this.showModal2CTA || this.showScheduleCTA);
  }

  ngAfterViewInit(): void {
    this.onWindowScroll();
  }

  ctaVideoPlay(): void {
    this.ctaVideoPlayEvent.emit();
  }

  activateModal(modalContent: string): void {
    this.modal.openModal({ showModal: true, modalContent: modalContent });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    // Skip scroll handling during SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.topSectionCta) {
      this.elementVisibility.checkElementVisiblity(this.topSectionCta.nativeElement);

      const currentCtaDisplay = this.ctaDisplay;
      if (this.lastCtaDisplayValue !== currentCtaDisplay) {
        this.lastCtaDisplayValue = currentCtaDisplay;
        this.ctaDisplayChange.emit(currentCtaDisplay);
      }
    }
  }
}
